export type Lang = "en" | "pl";

export type CopyKey = keyof typeof COPY.en;

export const COPY = {
  en: {
    metaTitle: "Barmaid - Premium hospitality, one scan away.",
    themeLabel: "Theme",
    themeSystem: "System",
    themeLight: "Light",
    themeDark: "Dark",
    langLabel: "Language",

    navHowItWorks: "How it works",
    navForVenues: "For venues",
    navWhyBarmaid: "Why Barmaid",
    navContact: "Contact",
    navGetStarted: "Get started",
    navLogin: "Log in",

    heroTitle: "Premium hospitality,",
    heroTitleAccent: "one scan away.",
    heroSubtitle:
      "QR orders for restaurants, bars, food venues, and more. Customers scan, order, and pay in their browser - staff run service from one live screen.",
    heroCtaPrimary: "Start for your venue",
    heroCtaSecondary: "See how it works",

    howItWorksTitle: "How it works for",
    howItWorksToggleGuests: "guests",
    howItWorksToggleVenue: "venues",
    howItWorksSubtitle:
      "No app download, no account. Guests order from their phone in four simple steps.",
    howItWorksVenueSubtitle:
      "From alert to handover. Your team runs orders from one live screen.",

    step1Title: "Scan the QR code",
    step1Body:
      "Open the camera on your phone and scan the QR at your table or pickup spot. The menu opens in your browser instantly.",
    step2Title: "Browse the menu and add items",
    step2Body:
      "Pick what you want from the menu. Add items, adjust quantities, and leave an optional note for the kitchen or pickup name.",
    step3Title: "Pay securely",
    step3Body:
      "Review your order and pay by card, BLIK, Apple Pay, and more on the secure checkout page. Optionally add an email for payment confirmation.",
    step4Title: "Track your order",
    step4Body:
      "After payment you land on a live status page. It updates automatically as the venue prepares your order. Enable push notifications to stay in the loop.",

    venueStep1Title: "New order alert",
    venueStep1Body:
      "A paid order appears on the live board immediately so nothing gets missed.",
    venueStep2Title: "Prepare order",
    venueStep2Body: "Follow the guest's order and prepare each item for pickup or delivery.",
    venueStep3Title: "Mark ready",
    venueStep3Body: "Mark the order ready when it is waiting for the guest.",
    venueStep4Title: "Scan & hand over",
    venueStep4Body: "Confirm pickup with a QR scan or the 6-digit order number.",

    forVenuesTitle: "Dashboard for managers and staff",
    forVenuesSubtitle:
      "Everything you need to accept QR orders, get paid, and hand over food and drinks - from any phone or tablet.",

    venueFeature1Title: "Live order tracking",
    venueFeature1Body:
      "Kanban workflow from paid to preparing, ready, and out for delivery. New orders trigger alerts so nothing gets missed.",
    venueFeature2Title: "Stripe Connect payments",
    venueFeature2Body:
      "Each venue has its own account. Accept card, BLIK, Apple Pay, and more. Payouts go automatically and directly to your business account.",
    venueFeature3Title: "Spots and QR codes",
    venueFeature3Body:
      "Create pickup or delivery spots - each with its own QR. Create separate menus for individual spots or groups.",
    venueFeature4Title: "Scan-verified handover",
    venueFeature4Body: "Confirm pickup with a QR code or a 6-digit order number.",
    venueFeature5Title: "Team and roles",
    venueFeature5Body:
      "Invite owners, managers, and staff with role-based access. Everyone sees what they need, nothing more.",
    venueFeature6Title: "Opening hours and pause",
    venueFeature6Body:
      "Let guests browse anytime but accept orders only during opening hours. Pause and resume ordering anytime for the whole venue or individual spots.",

    whyTitle: "Why venues choose Barmaid",
    whySubtitle: "Purpose-built for Polish hospitality - outdoor service, fast turnover, and real-world compliance.",

    why1Title: "No app download",
    why1Body: "Guests order in the browser. Works on any smartphone - no App Store friction.",
    why2Title: "BLIK and cards",
    why2Body: "Pay by card, BLIK, Apple Pay, and more.",
    why5Title: "Real-time updates",
    why5Body: "Live order status for guests and instant notifications for staff when orders come in.",
    why6Title: "Ready-made solution",
    why6Body: "No unnecessary steps. All you need are QR codes - and you're ready to go.",

    ctaTitle: "Ready to accept QR orders?",
    ctaSubtitle: "Set up your menu, print QR codes, and start taking payments in minutes.",
    ctaButton: "Create account",
    ctaContactOr: "or",
    ctaContactLink: "Get in touch",

    contactMetaTitle: "Contact Barmaid - Get in touch",
    privacyPageTitle: "Privacy Policy - Barmaid",
    privacyPageUpdated: "Version dated 13 July 2026.",
    contactSectionTitle: "Get in touch",
    contactSectionSubtitle:
      "Tell us about your venue and we'll help you get started with QR ordering.",
    contactPageTitle: "Contact us",
    contactPageSubtitle:
      "Registration is currently by invitation. Share your venue details and we'll reach out within one business day.",
    contactLabelFirstName: "First name",
    contactLabelLastName: "Last name",
    contactLabelEmail: "Email",
    contactLabelPhone: "Phone (optional)",
    contactLabelCompany: "Venue name",
    contactLabelMessage: "Message (optional)",
    contactPlaceholderFirstName: "Jane",
    contactPlaceholderLastName: "Smith",
    contactPlaceholderEmail: "you@venue.com",
    contactPlaceholderPhone: "+48 123 456 789",
    contactPlaceholderMessage: "Tell us about your venue or ask a question",
    contactSubmit: "Send message",
    contactSubmitting: "Sending...",
    contactSuccessTitle: "Message sent",
    contactSuccessBody: "Thanks for reaching out. We'll be in touch within one business day.",
    contactErrorFirstNameRequired: "Please enter your first name.",
    contactErrorLastNameRequired: "Please enter your last name.",
    contactErrorEmailRequired: "Please enter your email.",
    contactErrorEmailInvalid: "Please enter a valid email address.",
    contactErrorCompanyRequired: "Please enter your venue name.",
    contactConsentPrivacyBefore: "I have read and accept the ",
    contactConsentPrivacyLink: "Privacy Policy",
    contactConsentPrivacyAfter:
      " and consent to the processing of my personal data and to being contacted about this inquiry.",
    contactConsentPrivacyLegal:
      "I have read and accept the Privacy Policy and consent to the processing of my personal data and to being contacted about this inquiry.",
    contactConsentOneToOneLegal:
      "I agree to be contacted by Barmaid about this inquiry.",
    contactConsentMarketing:
      "I agree to receive marketing communications from Barmaid about products, updates, and offers.",
    contactConsentMarketingLegal:
      "I agree to receive marketing communications from Barmaid about products, updates, and offers.",
    contactErrorPrivacyRequired: "Please accept the Privacy Policy to continue.",
    contactErrorSubmit: "Something went wrong. Please try again.",
    contactErrorNotConfigured: "Contact form is not configured yet. Please email us directly.",

    footerTagline: "QR ordering for modern hospitality.",
    footerContact: "Contact",
    footerLogin: "Staff login",
    footerPrivacy: "Privacy",
    footerTerms: "Terms",
    footerCopyright: "© {year} Barmaid. All rights reserved.",
  },
  pl: {
    metaTitle: "Barmaid - Premium hospitality, na jeden skan.",
    themeLabel: "Motyw",
    themeSystem: "System",
    themeLight: "Jasny",
    themeDark: "Ciemny",
    langLabel: "Język",

    navHowItWorks: "Jak to działa",
    navForVenues: "Dla lokali",
    navWhyBarmaid: "Dlaczego Barmaid",
    navContact: "Kontakt",
    navGetStarted: "Zacznij",
    navLogin: "Zaloguj się",

    heroTitle: "Premium hospitality,",
    heroTitleAccent: "na jeden skan.",
    heroSubtitle:
      "Zamówienia z QR dla restauracji, barów, lokali gastronomicznych i nie tylko. Goście skanują, zamawiają i płacą w przeglądarce - obsługa prowadzi serwis z jednego ekranu.",
    heroCtaPrimary: "Uruchom w swoim lokalu",
    heroCtaSecondary: "Zobacz jak to działa",

    howItWorksTitle: "Jak to działa dla",
    howItWorksToggleGuests: "gości",
    howItWorksToggleVenue: "lokalu",
    howItWorksSubtitle:
      "Bez instalacji aplikacji i konta. Goście zamawiają z telefonu w czterech prostych krokach.",
    howItWorksVenueSubtitle:
      "Od alertu do wydania. Twój zespół prowadzi zamówienia z jednego ekranu.",

    step1Title: "Zeskanuj kod QR",
    step1Body:
      "Otwórz aparat w telefonie i zeskanuj kod QR przy stoliku lub miejscu odbioru. Menu otworzy się od razu w przeglądarce.",
    step2Title: "Przeglądaj menu i dodawaj pozycje",
    step2Body:
      "Wybierz dania z menu. Dodawaj pozycje, zmieniaj ilość i opcjonalnie zostaw notatkę dla kuchni lub imię do odbioru.",
    step3Title: "Zapłać bezpiecznie",
    step3Body:
      "Sprawdź zamówienie i zapłać kartą, BLIK-iem, Apple Pay i innymi na bezpiecznej stronie płatności. Możesz też podać e-mail na potwierdzenie.",
    step4Title: "Śledź zamówienie",
    step4Body:
      "Po płatności trafisz na stronę ze statusem na żywo. Aktualizuje się automatycznie, gdy lokal przygotowuje zamówienie. Włącz powiadomienia push, by być na bieżąco.",

    venueStep1Title: "Alert nowego zamówienia",
    venueStep1Body:
      "Opłacone zamówienie pojawia się od razu na tablicy, więc nic nie umknie.",
    venueStep2Title: "Przygotuj zamówienie",
    venueStep2Body: "Przygotuj pozycje zgodnie z zamówieniem gościa do odbioru lub dostawy.",
    venueStep3Title: "Oznacz jako gotowe",
    venueStep3Body: "Oznacz zamówienie jako gotowe, gdy czeka na gościa.",
    venueStep4Title: "Zeskanuj i wydaj",
    venueStep4Body: "Potwierdź odbiór skanem kodu QR lub 6-cyfrowym numerem zamówienia.",

    forVenuesTitle: "Dashboard dla managerów i obsługi",
    forVenuesSubtitle:
      "Wszystko, czego potrzebujesz, by przyjmować zamówienia QR, otrzymywać płatności i wydawać jedzenie oraz napoje - z dowolnego telefonu lub tabletu.",

    venueFeature1Title: "Śledzenie zamówień na żywo",
    venueFeature1Body:
      "Przepływ kanban: opłacone → w przygotowaniu → gotowe → w drodze. Nowe zamówienia wywołują alerty, więc nic nie umknie.",
    venueFeature2Title: "Płatności Stripe Connect",
    venueFeature2Body:
      "Każdy lokal posiada własne konto. Przyjmuj płatności kartami, BLIK, Apple Pay i innymi. Wypłacaj środki automatycznie i bezpośrednio na konto firmy.",
    venueFeature3Title: "Miejsca i kody QR",
    venueFeature3Body:
      "Twórz miejsca odbioru lub dostawy - każde z własnym QR. Twórz osobne menu dla poszczególnych miejsc lub grup.",
    venueFeature4Title: "Wydanie ze skanem",
    venueFeature4Body: "Potwierdzaj odbiór kodem QR, lub 6-cyfrowym numerem zamówienia.",
    venueFeature5Title: "Zespół i role",
    venueFeature5Body:
      "Zapraszaj właścicieli, managerów i obsługę z dostępem opartym na rolach. Każdy widzi tylko to, czego potrzebuje.",
    venueFeature6Title: "Godziny otwarcia i pauza",
    venueFeature6Body:
      "Pozwól przeglądać menu o każdej porze, ale przyjmuj zamówienia tylko w godzinach otwarcia. Wstrzymuj i wznawiaj zamawianie w dowolnym momencie dla całego lokalu lub poszczególnych miejsc.",

    whyTitle: "Dlaczego lokale wybierają Barmaid",
    whySubtitle:
      "Zaprojektowane pod polską gastronomię - obsługa na zewnątrz, szybki obrót i zgodność z przepisami.",

    why1Title: "Bez instalacji aplikacji",
    why1Body: "Goście zamawiają w przeglądarce. Działa na każdym smartfonie - bez App Store.",
    why2Title: "BLIK i karty",
    why2Body: "Płatności kartą, BLIK-iem, Apple Pay i innymi.",
    why5Title: "Aktualizacje na żywo",
    why5Body: "Status zamówienia na żywo dla gości i natychmiastowe powiadomienia dla obsługi.",
    why6Title: "Gotowe rozwiązanie",
    why6Body: "Bez zbędnych kroków. Wystarczą kody QR — i możesz działać.",

    ctaTitle: "Gotowy na zamówienia QR?",
    ctaSubtitle: "Skonfiguruj menu, wydrukuj kody QR i zacznij przyjmować płatności w kilka minut.",
    ctaButton: "Załóż konto",
    ctaContactOr: "lub",
    ctaContactLink: "Napisz do nas",

    contactMetaTitle: "Kontakt z Barmaid - Napisz do nas",
    privacyPageTitle: "Polityka prywatności - Barmaid",
    privacyPageUpdated: "Wersja z 13 lipca 2026 r.",
    contactSectionTitle: "Napisz do nas",
    contactSectionSubtitle:
      "Opowiedz nam o swoim lokalu, a pomożemy Ci uruchomić zamawianie QR.",
    contactPageTitle: "Kontakt",
    contactPageSubtitle:
      "Rejestracja odbywa się obecnie na zaproszenie. Podaj dane lokalu, a odezwiemy się w ciągu jednego dnia roboczego.",
    contactLabelFirstName: "Imię",
    contactLabelLastName: "Nazwisko",
    contactLabelEmail: "E-mail",
    contactLabelPhone: "Telefon (opcjonalnie)",
    contactLabelCompany: "Nazwa lokalu",
    contactLabelMessage: "Wiadomość (opcjonalnie)",
    contactPlaceholderFirstName: "Jan",
    contactPlaceholderLastName: "Kowalski",
    contactPlaceholderEmail: "ty@lokal.pl",
    contactPlaceholderPhone: "+48 123 456 789",
    contactPlaceholderMessage: "Opowiedz o lokalu lub zadaj pytanie",
    contactSubmit: "Wyślij wiadomość",
    contactSubmitting: "Wysyłanie...",
    contactSuccessTitle: "Wiadomość wysłana",
    contactSuccessBody: "Dziękujemy za kontakt. Odezwiemy się w ciągu jednego dnia roboczego.",
    contactErrorFirstNameRequired: "Podaj imię.",
    contactErrorLastNameRequired: "Podaj nazwisko.",
    contactErrorEmailRequired: "Podaj adres e-mail.",
    contactErrorEmailInvalid: "Podaj prawidłowy adres e-mail.",
    contactErrorCompanyRequired: "Podaj nazwę lokalu.",
    contactConsentPrivacyBefore: "Zapoznałem(-am) się i akceptuję ",
    contactConsentPrivacyLink: "Politykę prywatności",
    contactConsentPrivacyAfter:
      " oraz wyrażam zgodę na przetwarzanie moich danych osobowych i na kontakt w sprawie tego zapytania.",
    contactConsentPrivacyLegal:
      "Zapoznałem(-am) się i akceptuję Politykę prywatności oraz wyrażam zgodę na przetwarzanie moich danych osobowych i na kontakt w sprawie tego zapytania.",
    contactConsentOneToOneLegal:
      "Wyrażam zgodę na kontakt ze strony Barmaid w sprawie tego zapytania.",
    contactConsentMarketing:
      "Wyrażam zgodę na otrzymywanie od Barmaid komunikacji marketingowej o produktach, aktualizacjach i ofertach.",
    contactConsentMarketingLegal:
      "Wyrażam zgodę na otrzymywanie od Barmaid komunikacji marketingowej o produktach, aktualizacjach i ofertach.",
    contactErrorPrivacyRequired: "Zaakceptuj Politykę prywatności, aby kontynuować.",
    contactErrorSubmit: "Coś poszło nie tak. Spróbuj ponownie.",
    contactErrorNotConfigured:
      "Formularz kontaktowy nie jest jeszcze skonfigurowany. Napisz do nas bezpośrednio.",

    footerTagline: "Zamawianie QR dla nowoczesnej gastronomii.",
    footerContact: "Kontakt",
    footerLogin: "Logowanie obsługi",
    footerPrivacy: "Prywatność",
    footerTerms: "Regulamin",
    footerCopyright: "© {year} Barmaid. Wszelkie prawa zastrzeżone.",
  },
} as const;

export interface HowItWorksStep {
  titleKey: CopyKey;
  bodyKey: CopyKey;
  icon: "scan" | "menu" | "pay" | "track" | "alert" | "prepare" | "ready" | "handover";
}

export const HOW_IT_WORKS_GUEST_STEPS: HowItWorksStep[] = [
  { icon: "scan", titleKey: "step1Title", bodyKey: "step1Body" },
  { icon: "menu", titleKey: "step2Title", bodyKey: "step2Body" },
  { icon: "pay", titleKey: "step3Title", bodyKey: "step3Body" },
  { icon: "track", titleKey: "step4Title", bodyKey: "step4Body" },
];

export const HOW_IT_WORKS_VENUE_STEPS: HowItWorksStep[] = [
  { icon: "alert", titleKey: "venueStep1Title", bodyKey: "venueStep1Body" },
  { icon: "prepare", titleKey: "venueStep2Title", bodyKey: "venueStep2Body" },
  { icon: "ready", titleKey: "venueStep3Title", bodyKey: "venueStep3Body" },
  { icon: "handover", titleKey: "venueStep4Title", bodyKey: "venueStep4Body" },
];

export interface VenueFeature {
  titleKey: CopyKey;
  bodyKey: CopyKey;
  icon: "orders" | "stripe" | "qr" | "handover" | "team" | "schedule";
}

export const VENUE_FEATURES: VenueFeature[] = [
  { icon: "orders", titleKey: "venueFeature1Title", bodyKey: "venueFeature1Body" },
  { icon: "stripe", titleKey: "venueFeature2Title", bodyKey: "venueFeature2Body" },
  { icon: "qr", titleKey: "venueFeature3Title", bodyKey: "venueFeature3Body" },
  { icon: "handover", titleKey: "venueFeature4Title", bodyKey: "venueFeature4Body" },
  { icon: "team", titleKey: "venueFeature5Title", bodyKey: "venueFeature5Body" },
  { icon: "schedule", titleKey: "venueFeature6Title", bodyKey: "venueFeature6Body" },
];

export interface WhyFeature {
  titleKey: CopyKey;
  bodyKey: CopyKey;
  icon: "noApp" | "blik" | "realtime" | "ready";
}

export const WHY_FEATURES: WhyFeature[] = [
  { icon: "noApp", titleKey: "why1Title", bodyKey: "why1Body" },
  { icon: "blik", titleKey: "why2Title", bodyKey: "why2Body" },
  { icon: "realtime", titleKey: "why5Title", bodyKey: "why5Body" },
  { icon: "ready", titleKey: "why6Title", bodyKey: "why6Body" },
];
